import useConfigureAxios from "@FRAMEWORK/utils/useConfigureAxios";
import publicRuntimeConfig from "@FRAMEWORK/config/public";
import {Response} from "@QUERIES/types";
import {UseQueryOptions, useQuery} from "@tanstack/react-query";

interface GoodsAvailEvent {
	evCd: string;
	evStartDt: string;
	eventMonthList: {
		minOutStartDt: string;
		maxOutStartDt: string;
	};
}

type SearchType = "amt" | "day";

const key = "useGoodsAvailEvent";
/** ```
 *  해당 상품의 가장 빠른 행사 , 일자 , 전시된 월
 *   `/prdt/api/goods/${goodsCd}/available-date/{searchType}`
 * parameter
 *   `goodsCd` 상품코드
 *   `searchType` 검색타입 amt | day
 * ```*/
const getPath = (goodsCd: string, searchType: SearchType): string | null => {
	if (!goodsCd) return null;
	return `${publicRuntimeConfig.ENV_PRODUCT_API_BASEPATH}/api/goods/${goodsCd}/available-date/${searchType}`;
};

/** ```
 *  해당 상품의 가장 빠른 행사 , 일자 , 전시된 월
 *   `/prdt/api/goods/${goodsCd}/available-date/{searchType}`
 * parameter
 *   `goodsCd` 상품코드
 *   `searchType` 검색타입 amt | day
 * ```*/
const goodsAvailEventAsync = async (goodsCd: string, searchType: SearchType = "day") => {
	const result = await useConfigureAxios(getPath(goodsCd, searchType), {method: "GET"});
	return result;
};

/** ```
 *  해당 상품의 가장 빠른 행사 , 일자 , 전시된 월
 *   `/prdt/api/goods/${goodsCd}/available-date/{searchType}`
 * parameter
 *   `goodsCd` 상품코드
 *   `searchType` 검색타입 amt | day
 * ```*/
const goodsAvailEventSSR = async (goodsCd: string, searchType: SearchType = "day") => {
	const result = await useConfigureAxios(publicRuntimeConfig.ENV_PRODUCT_API_URL + getPath(goodsCd, searchType), {
		method: "GET",
	});
	return result;
};

/** ```
 *  해당 상품의 가장 빠른 행사 , 일자 , 전시된 월
 *   `/prdt/api/goods/${goodsCd}/available-date/{searchType}`
 * parameter
 *   `goodsCd` 상품코드
 *   `searchType` 검색타입 amt | day
 * ```*/
const useGoodsAvailEvent = (
	goodsCd: string,
	searchType: SearchType = "day",
	options?: Partial<UseQueryOptions<Response<GoodsAvailEvent>, Error>>,
) =>
	useQuery({
		queryKey: [key, getPath(goodsCd, searchType)],
		queryFn: () => useConfigureAxios({url: getPath(goodsCd, searchType)}).then(r => r.data),
		staleTime: 1000 * 30 * 60,
		...options,
	});

export default useGoodsAvailEvent;
export {getPath, key, goodsAvailEventAsync, goodsAvailEventSSR};
export type {GoodsAvailEvent};
